import React from 'react';
import { Copy, Check, HelpCircle } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import Tooltip from '../ui/Tooltip';
import { isWindows } from '../../utils/nativePrint';
import { MCP_AGENTS, loadAgentChoice, saveAgentChoice } from './mcpAgentChoice';

/**
 * The client configurations (**D8**), all with the token filled in. Since
 * 26.10.2026 the page asks which agent the user has (Claude Code, Cursor,
 * Codex, Claude Desktop, other) and shows only that one's setup.
 *
 * **Why there are two, stated to the user rather than buried in a plan:**
 * Claude Desktop, web and mobile open a remote MCP connection from Anthropic's
 * cloud even when Desktop is running on this machine, so they cannot reach
 * `127.0.0.1` and need the helper program. Claude Code runs here and takes the
 * URL directly.
 *
 * **Block (a) is unaffected by whether a bridge is installed** — it needs
 * none, and the page says so, so a user who cannot install one (offline, a
 * proxy that blocks the CDN, a build from before bridges were published) still
 * has a working path.
 *
 * **Block (b) is only rendered when a bridge is actually installed.** A config
 * containing `<absolute path to the bridge>` is a config that silently fails,
 * which is worse than a visible explanation.
 *
 * **The token rides `env`, never `args`.** Process argv is world-readable on
 * Linux through `/proc/<pid>/cmdline` and visible in `ps` to any process of
 * the same user on macOS; a config file is at least `0600`-able. That is a
 * real difference, not a style choice.
 *
 * The blocks themselves are `<pre>` content — code, not translated strings.
 */
export function CopyBlock({ text, label, copiedLabel }) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef(null);
  // The timeout has to be cleared on unmount: the settings modal closes while
  // this is pending, and a setState on an unmounted component is a warning that
  // CRA's `setupTests.js` turns into console noise for every later test.
  React.useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // A clipboard the OS refused is not worth an error state — the text is
      // selectable right there.
    }
  };
  return (
    <div className="mcp-codeblock">
      {/* The app's own scrollbar (OsScroll), not the native one: a long
          command (the token line) scrolls horizontally inside the box. */}
      <OsScroll className="mcp-code-scroll">
        <pre className="mcp-code">{text}</pre>
      </OsScroll>
      {/* Icon ONLY, in a fixed square. A label that swapped "Copy" → "Copied"
          resized the button under the pointer on every click. The state now
          lives in the icon, and the words move to `aria-label`/`title` so the
          control keeps an accessible name (an icon-only button otherwise has
          none — a Tooltip wrapper is not one; see 50-gotchas.md).
          The tick's colour is passed explicitly, matching `EnvironmentsModal`'s
          save tick (`#4ade80`). It is NOT the `text-green-500 dark:...` pair
          `ToolCodeBlock.js:52` uses: `.text-green-500` does not exist in
          `index.css` and `dark:*` silently no-ops there, so that tick renders
          in the inherited colour. Chipped separately. */}
      <button
        type="button"
        className="settings-action-btn settings-action-btn--icon"
        onClick={copy}
        aria-label={copied ? copiedLabel : label}
        title={copied ? copiedLabel : label}
      >
        {copied ? <Check size={13} color="#4ade80" strokeWidth={2.5} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

/**
 * ONE line: a `\` line continuation only works in bash/zsh — pasted into
 * Windows cmd or PowerShell it breaks the command. The box scrolls sideways.
 *
 * **`--scope user` is load-bearing (17.09.2026).** Without it `claude mcp add`
 * writes the server into Claude Code's LOCAL scope, which is per-directory:
 * `~/.claude.json` grows a separate `apiboo` entry, with its own copy of the
 * token, under `projects["<the directory it was run in>"]`. Run the command in
 * two projects and there are two independent copies; regenerate the token and
 * every one of them silently keeps the dead one, in directories nobody can
 * enumerate.
 *
 * The symptom is the worst kind: the server answers 401 while THIS page looks
 * perfectly correct, because the token here is the current one and the client's
 * is not. It cost an evening to diagnose on our own machine, where three scopes
 * held three different tokens and only the one nobody worked in still matched.
 *
 * `--scope user` stores it once, globally, and every directory inherits it — so
 * a regenerate is one paste rather than a hunt.
 */
export function httpCommand(endpoint, token) {
  return `claude mcp add --scope user --transport http apiboo ${endpoint} --header "Authorization: Bearer ${token}"`;
}

/**
 * Any client that speaks MCP over HTTP (Cursor, Zed, VS Code, …): the two
 * facts every one of them asks for, in whatever form its own config takes
 * (card 80fa086c — the heading promised more clients than the page served).
 */
export function genericHttpSnippet(endpoint, token) {
  return `URL:     ${endpoint}\nHeader:  Authorization: Bearer ${token}`;
}

export function desktopConfig(bridgePath, endpoint, token) {
  return JSON.stringify(
    {
      mcpServers: {
        apiboo: {
          command: bridgePath,
          args: ['--url', endpoint],
          env: { APIBOO_MCP_TOKEN: token },
        },
      },
    },
    null,
    2
  );
}

/**
 * OpenAI Codex CLI. `codex mcp add` has no flag for a literal token or header:
 * for a streamable HTTP server it takes `--url` and `--bearer-token-env-var`,
 * and stores only the variable's NAME in `~/.codex/config.toml`
 * (`codex-rs/cli/src/mcp_cmd.rs`, checked 26.10.2026). So the token reaches
 * Codex through the environment, and this is two steps: set the variable,
 * then add the server.
 */
export const CODEX_TOKEN_VAR = 'APIBOO_MCP_TOKEN';

export function codexAddCommand(endpoint) {
  return `codex mcp add apiboo --url ${endpoint} --bearer-token-env-var ${CODEX_TOKEN_VAR}`;
}

/**
 * `setx` on Windows (cmd and PowerShell alike) writes the USER environment, so
 * every terminal opened afterwards has it. Elsewhere an `export` line, which
 * the copy beside it says belongs in the shell profile.
 */
export function codexTokenCommand(token, windows) {
  return windows ? `setx ${CODEX_TOKEN_VAR} "${token}"` : `export ${CODEX_TOKEN_VAR}="${token}"`;
}

/** The one-paste alternative: `http_headers` carries the literal header. */
export function codexConfigToml(endpoint, token) {
  return `[mcp_servers.apiboo]\nurl = "${endpoint}"\nhttp_headers = { "Authorization" = "Bearer ${token}" }`;
}

/**
 * Cursor has no `mcp add` command; remote servers go into `~/.cursor/mcp.json`
 * as `url` + `headers` (cursor.com/docs/context/mcp).
 *
 * No "Add to Cursor" deep link: Cursor documents one
 * (`cursor://anysphere.cursor-deeplink/mcp/install?…`), but the desktop app
 * opens URLs through the Tauri shell plugin, whose default scope only lets
 * http(s), mailto and tel through, so the button would do nothing. Widening
 * that scope is a separate decision.
 */
export function cursorConfig(endpoint, token) {
  return JSON.stringify(
    { mcpServers: { apiboo: { url: endpoint, headers: { Authorization: `Bearer ${token}` } } } },
    null,
    2
  );
}

/**
 * "Which agent do you use?" — a radio group drawn as the settings segmented
 * control. Arrow keys move AND select (the WAI-ARIA radio pattern), and only
 * the checked option is in the tab order.
 */
function AgentPicker({ t, value, onChange }) {
  const refs = React.useRef([]);
  const index = MCP_AGENTS.findIndex((a) => a.id === value);
  const move = (to) => {
    const next = (to + MCP_AGENTS.length) % MCP_AGENTS.length;
    onChange(MCP_AGENTS[next].id);
    refs.current[next]?.focus();
  };
  const onKeyDown = (e) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (step) move(index + step);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(MCP_AGENTS.length - 1);
    else return;
    e.preventDefault();
  };
  return (
    <div className="mcp-agent-pick">
      <div className="settings-row-label" id="mcp-agent-question">{t('mcp.connectAgentQuestion')}</div>
      <div
        className="settings-segmented mcp-agent-picker"
        role="radiogroup"
        aria-labelledby="mcp-agent-question"
        onKeyDown={onKeyDown}
      >
        {MCP_AGENTS.map((a, i) => {
          const on = a.id === value;
          return (
            <button
              key={a.id}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              className={`settings-segmented-btn${on ? ' settings-segmented-btn--active' : ''}`}
              onClick={() => onChange(a.id)}
            >
              {a.labelKey ? t(a.labelKey) : a.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Snippet({ t, desc, text }) {
  return (
    <div className="mcp-block">
      {desc && <div className="settings-row-desc">{desc}</div>}
      <CopyBlock text={text} label={t('mcp.connectCopy')} copiedLabel={t('mcp.connectCopied')} />
    </div>
  );
}

function ClaudeCodeSetup({ t, endpoint, token }) {
  return (
    <div className="mcp-block">
      <div className="settings-row-desc">
        {t('mcp.connectHttpDesc')}
        {/* The cure for the failure `httpCommand` documents above, put where
            the command is rather than in a doc comment only we read: a stale
            entry answers 401 while THIS page shows a perfectly good token,
            and the page gave no hint that an old one might still exist
            (owner, 23.09.2026).

            A tooltip rather than prose on the page: it is a recovery step,
            irrelevant to anyone connecting for the first time.

            The commands carry NO token — removal never needs one, and a
            token belongs in the copy blocks, not in a hint. */}
        <Tooltip
          side="top"
          content={
            <span className="mcp-help-tip">
              <span>{t('mcp.connectHttpStaleHelp')}</span>
              <code>claude mcp remove apiboo --scope user</code>
              <span>{t('mcp.connectHttpStaleLocal')}</span>
              <code>claude mcp remove apiboo</code>
            </span>
          }
        >
          {/* Its own aria-label: Tooltip only lends one when `content` is a
              string, and this one is a node. */}
          <span className="mcp-help-dot" tabIndex={0} role="button" aria-label={t('mcp.connectHttpStaleAria')}>
            <HelpCircle size={12} aria-hidden />
          </span>
        </Tooltip>
      </div>
      <CopyBlock text={httpCommand(endpoint, token)} label={t('mcp.connectCopy')} copiedLabel={t('mcp.connectCopied')} />
    </div>
  );
}

function CodexSetup({ t, endpoint, token }) {
  const windows = isWindows();
  return (
    <>
      <Snippet
        t={t}
        desc={windows ? t('mcp.connectCodexTokenWindows') : t('mcp.connectCodexTokenUnix')}
        text={codexTokenCommand(token, windows)}
      />
      <Snippet t={t} desc={t('mcp.connectCodexAdd')} text={codexAddCommand(endpoint)} />
      <Snippet t={t} desc={t('mcp.connectCodexToml')} text={codexConfigToml(endpoint, token)} />
    </>
  );
}

function ClaudeDesktopSetup({ t, endpoint, token, bridgePath, bridgeInstall }) {
  return (
    <>
      <div className="settings-row-desc mcp-note">{t('mcp.connectDesktopWhy')}</div>
      {bridgeInstall}
      <div className="mcp-block">
        {bridgePath ? (
          <>
            <div className="settings-row-desc">{t('mcp.connectStdioDesc')}</div>
            <CopyBlock
              text={desktopConfig(bridgePath, endpoint, token)}
              label={t('mcp.connectCopy')}
              copiedLabel={t('mcp.connectCopied')}
            />
          </>
        ) : (
          <div className="settings-row-desc">{t('mcp.connectStdioUnavailable')}</div>
        )}
      </div>
    </>
  );
}

/**
 * Only the chosen agent's setup is mounted: five sets of instructions on one
 * page read as five things to do. `bridgeInstall` (the helper program row) is
 * passed in by the caller and shown under Claude Desktop, the one client that
 * needs it.
 */
export default function McpClientConfigs({ t, endpoint, token, bridgePath, bridgeInstall = null }) {
  const [agent, setAgent] = React.useState(loadAgentChoice);
  const choose = (id) => {
    setAgent(id);
    saveAgentChoice(id);
  };
  const shownToken = token || t('mcp.tokenPlaceholder');
  const props = { t, endpoint, token: shownToken };
  return (
    <>
      <AgentPicker t={t} value={agent} onChange={choose} />
      <div className="mcp-agent-setup" data-agent={agent}>
        {agent === 'claude-code' && <ClaudeCodeSetup {...props} />}
        {agent === 'cursor' && <Snippet t={t} desc={t('mcp.connectCursorDesc')} text={cursorConfig(endpoint, shownToken)} />}
        {agent === 'codex' && <CodexSetup {...props} />}
        {agent === 'claude-desktop' && (
          <ClaudeDesktopSetup {...props} bridgePath={bridgePath} bridgeInstall={bridgeInstall} />
        )}
        {agent === 'other' && <Snippet t={t} desc={t('mcp.connectGenericDesc')} text={genericHttpSnippet(endpoint, shownToken)} />}
      </div>
    </>
  );
}

